import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

// USER_PROFILE_SPEC — single client for everything on the Profile page:
// identity, preferences, notifications, income targets, security.

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  avatarUrl: string | null;
  bio: string | null;
  location: string | null;
  timezone: string | null;
  // SCRUM-557 — phone (E.164) + any pending email change awaiting verification.
  phone?: string | null;
  pendingEmail?: string | null;
}

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  bio?: string;
  location?: string;
  timezone?: string;
  // SCRUM-557 — phone is null to clear; email triggers verification flow.
  phone?: string | null;
  email?: string;
}

export interface NotificationPrefs {
  morning_brief: boolean;
  analysis_auto_send: boolean;
  price_alerts: boolean;
  earnings_alerts: boolean;
  insider_alerts: boolean;
  cult_alerts: boolean;
  weekly_summary: boolean;
}

export interface IncomeTargets {
  weekly_target_usd: number | string | null;
  monthly_target_usd: number | string | null;
}

export interface Session {
  id: string;
  created_at: string;
  expires_at: string;
  // SCRUM-564: set by the backend on the row whose refresh token matches the
  // access JWT in the request that fetched the list. Frontend uses it to
  // label "This device" and hide the Revoke button on that row.
  is_current?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  constructor(private readonly http: HttpClient) {}

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>('/api/auth/profile');
  }
  // SCRUM-557: switched from PUT to PATCH so the request body can carry only
  // the fields the user actually changed (PATCH is what the spec / backend
  // documented endpoint for partial updates), and the response can include
  // emailChangeRequested + pendingEmail for the verification banner.
  updateProfile(body: ProfileUpdate): Observable<any> {
    return this.http.patch<any>('/api/auth/profile', body);
  }

  uploadAvatar(file: File): Observable<{ avatarUrl: string; thumbUrl: string }> {
    const fd = new FormData();
    fd.append('avatar', file);
    return this.http.post<{ avatarUrl: string; thumbUrl: string }>(
      '/api/auth/profile/avatar', fd
    );
  }
  deleteAvatar(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>('/api/auth/profile/avatar');
  }

  getPreferences(): Observable<any> {
    return this.http.get<any>('/api/auth/preferences');
  }
  updatePreferences(body: any): Observable<any> {
    return this.http.patch<any>('/api/auth/preferences', body);
  }

  getNotifications(): Observable<NotificationPrefs> {
    return this.http.get<NotificationPrefs>('/api/auth/preferences/notifications');
  }
  updateNotifications(body: Partial<NotificationPrefs>): Observable<any> {
    return this.http.patch<any>('/api/auth/preferences/notifications', body);
  }
  sendTestNotification(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/test-notification', {});
  }

  getIncomeTargets(): Observable<IncomeTargets> {
    return this.http.get<IncomeTargets>('/api/income-targets');
  }
  updateIncomeTargets(body: Partial<IncomeTargets>): Observable<any> {
    return this.http.patch<any>('/api/income-targets', body);
  }

  getDocuments(): Observable<{ documents: any[] }> {
    return this.http.get<{ documents: any[] }>('/api/auth/documents');
  }

  getSessions(): Observable<{ sessions: Session[] }> {
    return this.http.get<{ sessions: Session[] }>('/api/auth/sessions');
  }
  // SCRUM-564: revoke a single device. Backend refuses to revoke the caller's
  // own session (409); the UI hides the Revoke button on the is_current row
  // so the error path is only hit by direct API users.
  revokeSession(sessionId: string): Observable<{ revoked: boolean; sessionId: string }> {
    return this.http.delete<{ revoked: boolean; sessionId: string }>(`/api/auth/sessions/${encodeURIComponent(sessionId)}`);
  }
  logoutAll(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/logout-all', {});
  }
  changePassword(body: { currentPassword: string; newPassword: string; confirmPassword: string }): Observable<any> {
    return this.http.post<any>('/api/auth/change-password', body);
  }
}
